import { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { MessageSquare, Send } from 'lucide-react';
import Avatar from '../../components/ui/Avatar';
import Spinner from '../../components/ui/Spinner';
import { getErrorMessage } from '../../lib/apiClient';
import { subscribeToThread } from '../../lib/realtime';
import { useAuthStore } from '../../store/authStore';
import { formatDate } from '../../utils/format';
import {
  insertShareChatMessage,
  listShareChatMessages,
  postShareChatMessage,
  resolveShareChatThread,
  shareChatKeys,
} from '../share/shareApi';

const POLL_INTERVAL_MS = 15_000;

/**
 * Chat for a public share link with `allowChat` enabled — reachable by any
 * logged-in visitor, not a specific invited identity. Same live-socket +
 * polling-fallback UX as FileChatPanel/SharedChatPanel, sourced from the
 * token-based `shares/public/:token/chat/...` endpoints instead.
 */
export default function PublicShareChatPanel({ token, password }) {
  const queryClient = useQueryClient();
  const meId = useAuthStore((s) => s.user?.id);
  const [live, setLive] = useState(false);
  const [draft, setDraft] = useState('');
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);

  const { data: thread } = useQuery({
    queryKey: shareChatKeys.thread(token),
    queryFn: () => resolveShareChatThread({ token, password }),
    staleTime: Infinity,
  });
  const threadId = thread?.id;

  const messagesQuery = useInfiniteQuery({
    queryKey: shareChatKeys.messages(threadId),
    enabled: Boolean(threadId),
    initialPageParam: 1,
    queryFn: ({ pageParam }) => listShareChatMessages({ token, page: pageParam }),
    getNextPageParam: (last) => (last.meta.hasNextPage ? last.meta.page + 1 : undefined),
    refetchInterval: live ? false : POLL_INTERVAL_MS,
  });

  const messages = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const m of (messagesQuery.data?.pages ?? []).flatMap((p) => p.data)) {
      if (!seen.has(m.id)) {
        seen.add(m.id);
        out.push(m);
      }
    }
    return out.reverse();
  }, [messagesQuery.data]);
  const newestId = messages.at(-1)?.id;

  useEffect(() => {
    if (!threadId) return undefined;
    return subscribeToThread(threadId, {
      onLive: setLive,
      onMessage: (msg) => insertShareChatMessage(queryClient, threadId, msg),
    });
  }, [threadId, queryClient]);

  useEffect(() => {
    if (!messagesQuery.isFetchingNextPage) {
      bottomRef.current?.scrollIntoView({ block: 'end' });
    }
  }, [newestId, messagesQuery.isFetchingNextPage]);

  const send = useMutation({
    mutationFn: (body) => postShareChatMessage({ token, body, password }),
    onSuccess: (msg) => insertShareChatMessage(queryClient, threadId, msg),
    onError: (err) => toast.error(getErrorMessage(err, 'Could not send message')),
  });

  const submit = () => {
    const body = draft.trim();
    if (!body || !threadId || send.isPending) return;
    setDraft('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    send.mutate(body);
  };

  if (!threadId || messagesQuery.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="flex h-80 min-h-0 flex-col rounded-lg border border-slate-200">
      <div className="scrollbar-thin min-h-0 flex-1 space-y-3 overflow-auto p-3">
        {messagesQuery.hasNextPage && (
          <button
            onClick={() => messagesQuery.fetchNextPage()}
            disabled={messagesQuery.isFetchingNextPage}
            className="mx-auto block rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-500 hover:bg-slate-200 disabled:opacity-50"
          >
            {messagesQuery.isFetchingNextPage ? 'Loading…' : 'Load older messages'}
          </button>
        )}

        {messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-slate-400">
            <MessageSquare className="h-8 w-8" />
            <p className="text-sm">No messages yet — start the conversation.</p>
          </div>
        ) : (
          messages.map((msg) => {
            const own = msg.senderId === meId;
            return (
              <div key={msg.id} className={clsx('flex gap-2', own && 'flex-row-reverse')}>
                {!own && (
                  <Avatar
                    name={msg.sender?.displayName}
                    className="h-7 w-7 bg-slate-200 text-[10px] font-semibold text-slate-600"
                  />
                )}
                <div className={clsx('min-w-0 max-w-[80%] space-y-0.5', own && 'text-right')}>
                  <p className="text-[11px] text-slate-400">
                    {!own && (
                      <span className="mr-1.5 font-medium text-slate-600">
                        {msg.sender?.displayName ?? 'Unknown'}
                      </span>
                    )}
                    {formatDate(msg.createdAt)}
                  </p>
                  <div
                    className={clsx(
                      'inline-block max-w-full whitespace-pre-wrap break-words rounded-2xl px-3 py-1.5 text-left text-sm',
                      own
                        ? 'rounded-br-md bg-brand-600 text-white'
                        : 'rounded-bl-md border border-slate-200 bg-slate-50 text-slate-700',
                    )}
                  >
                    {msg.body}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      <div className="flex items-end gap-2 border-t border-slate-200 p-3">
        <textarea
          ref={textareaRef}
          rows={1}
          value={draft}
          placeholder="Write a message…"
          onChange={(e) => {
            setDraft(e.target.value);
            e.target.style.height = 'auto';
            e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          className="min-h-9 flex-1 resize-none rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none"
        />
        <button
          onClick={submit}
          disabled={!draft.trim() || send.isPending}
          aria-label="Send message"
          className="rounded-lg bg-brand-600 p-2 text-white transition-colors hover:bg-brand-400 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
